


import { makeStyles } from 'tss-react/mui';
import React, { useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { Box, Grid, KeyValue, MUIDataTable, MUIDataTableColumnDef, Typography } from 'web-library';
import { SelectedAppealSubjectInfoInSummary } from '../../../../api/candidate/appeal/model';
import FormHeader from '../../../../components/form-items/FormHeader';

const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#subject-review-table .MuiTableCell-root': {
      '&[data-colindex="5"]': {
        width: 250,
      },
    },
    '#subject-review-table .MuiTableCell-head': {
      '&[data-colindex="5"]': {
        textAlign: 'right',
      },
    },
    tr: {
      '&:not(:last-of-type)': {
        borderBottom: `1px solid ${theme.color.border.borderAlt} !important`,
      },
    },
  },
  container: {
    marginBottom: theme.spacing(7),
  },
  subHeaderContainer: {
    maxWidth: 960,
  },
  gridContainer: {
    marginBottom: theme.spacing(4),
  },
  priceGrid: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  feeCell: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  feeColumnHeader: {
    textAlign: 'right',
  },
}));

const SelectedSubjectsValues = ({ id, value }: { id?: string; value: string }) => (
  <Typography id={id} variant="body1">
    {value}
  </Typography>
);

const AppealFeesPayable = ({
  classes,
  t,
}: {
  classes: Record<string,string>;
  t: TFunction<'translation'>;
}) => (
  <Grid container justify="flex-end" className={classes.feeColumnHeader}>
    {t('appeal->>AppealSummary.SelectedSubjects.AppealFeesPayable')}
  </Grid>
);

const AppealFee = ({
  classes,
  rowIndex,
  appealFee,
}: {
  classes: Record<string,string>;
  rowIndex: number;
  appealFee: string;
}) => (
  <Box className={classes.feeCell}>
    <Typography id={`text-examFees-row${rowIndex}`} variant="body1">
      {`S$${appealFee}`}
    </Typography>
  </Box>
);

const SelectedSubjectsReview: React.FC<SelectedAppealSubjectInfoInSummary> = ({
  ...props
}: SelectedAppealSubjectInfoInSummary) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { subjectInfo = [] } = props;
  /** Datagrid columns
   * */
  const columns: MUIDataTableColumnDef[] = useMemo(
    () => [
      {
        name: 'subjectName',
        label: t('appeal->>AppealSummary.SelectedSubjects.SubjectName'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number) => {
            const { nameCode, nameDescription } = subjectInfo[dataIndex];
            const subjectInfoValue = nameDescription.concat(' ', nameCode);
            return SelectedSubjectsValues({
              value: subjectInfoValue,
            });
          },
        },
      },
      {
        name: 'examSeries',
        label: t('appeal->>AppealSummary.SelectedSubjects.GradeAlphabetical'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number) =>
            SelectedSubjectsValues({
              value: subjectInfo[dataIndex].gradeAlphabetical,
            }),
        },
      },
      {
        name: 'examLevel',
        label: t('appeal->>AppealSummary.SelectedSubjects.GradeNumerical'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            SelectedSubjectsValues({
              id: `text-examLevel-row${rowIndex}`,
              value: subjectInfo[dataIndex].gradeNumerical || 'N/A',
            }),
        },
      },

      {
        name: 'languageMedium',
        label: t('appeal->>AppealSummary.SelectedSubjects.LanguageMedium'),
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customBodyRenderLite: (dataIndex: number) =>
            SelectedSubjectsValues({
              value: subjectInfo[dataIndex].languageMedium,
            }),
        },
      },
      {
        name: 'examFee',
        disableClickEventBubbling: true,
        options: {
          filter: false,
          customHeadLabelRender: () => AppealFeesPayable({ classes, t }),
          customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
            AppealFee({
              classes,
              rowIndex,
              appealFee: subjectInfo[dataIndex].appealFee,
            }),
        },
      },
    ],
    [t, subjectInfo, classes]
  );

  return (
    <Grid className={classes.container}>
      <FormHeader title={t('appeal->>AppealSummary.SelectedSubjects.SelectedSubjects')} />

      <Grid container className={classes.subHeaderContainer} xs={12}>
        <Grid xs={12} sm={12} md={6} container direction="column">
          <KeyValue
            id="examYear"
            keyText={t('components->> SelectedSubjects.Exam Year')}
            valueText={props.examYear}
          />
        </Grid>
      </Grid>
      <Grid container className={classes.subHeaderContainer} xs={12}>
        <Grid xs={12} sm={12} md={6} container direction="column">
          <KeyValue
            id="examLevel"
            keyText={t('components->> SelectedSubjects.Exam Level')}
            valueText={props.examLevel}
          />
        </Grid>
        <Grid xs={12} sm={12} md={6} container direction="column">
          <KeyValue
            id="examSeries"
            keyText={t('components->> SelectedSubjects.Exam Series')}
            valueText={props.examSeries}
          />
        </Grid>
      </Grid>
      <Grid className={classes.gridContainer} xs={12}>
        <Box id="subject-review-table">
          <MUIDataTable columns={columns} data={props.subjectInfo} options={[]} components={[]} />
        </Box>
      </Grid>
    </Grid>
  );
};
export default SelectedSubjectsReview;
